"use client";

import { motion } from "framer-motion";
import { Container } from "@/components/ui/container";
import { Section } from "@/components/ui/section";
import { Accordion, AccordionItem } from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

interface FAQ {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  title?: string;
  description?: string;
  faqs: FAQ[];
  background?: "white" | "gray" | "dark";
  titleSize?: "default" | "large";
}

export function FAQSection({
  title = "Frequently Asked Questions",
  description,
  faqs,
  background = "white",
  titleSize = "default",
}: FAQSectionProps) {
  const isDark = background === "dark";

  return (
    <Section background={background}>
      <Container size="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2
            className={cn(
              "mb-4",
              titleSize === "large" ? "text-display-1" : "text-display-1",
              "text-ink",
            )}
          >
            {title}
          </h2>
          {description && (
            <p
              className={cn(
                "text-body-lg max-w-2xl mx-auto",
                isDark && "text-muted",
              )}
            >
              {description}
            </p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Accordion variant={isDark ? "dark" : "light"}>
            {faqs.map((faq, index) => (
              <AccordionItem
                key={faq.question}
                title={faq.question}
                defaultOpen={index === 0}
                variant={isDark ? "dark" : "light"}
              >
                {faq.answer}
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </Container>
    </Section>
  );
}
